import { ShieldCheck, Zap, Radar } from 'lucide-react';
import { toast } from 'sonner';
import { compressImage, pipeLatLngs } from '../lib/model';
import { num } from '../lib/utility';
import { extRtk, measurePrecise } from '../lib/precision';
import { IJ_ST, CAP_ST, KKS_LIMIT, mv, kksOk, CF_TYPES, CF_METHODS, CF_VOLT, CF_PHASES, CF_CABLES, cfDev, LOC_METHODS, nearestPipe, kksHist, kksTrend, kksChart, xbText } from '../lib/inspect';

const Num = ({ testid, value, ph, onChange }) => <input inputMode="decimal" data-testid={testid} value={value ?? ''} placeholder={ph} onChange={(e) => onChange(e.target.value.replace(',', '.'))} />;
const Chips = ({ list, value, onPick, lbl, testid }) => (
  <div className="chip-group">{list.map((k) => <button key={k} className={`chip seg ${value === k ? 'on' : ''}`} data-testid={`${testid}-${k}`} onClick={() => onPick(k)}>{lbl(k)}</button>)}</div>
);

function KksHistory({ t, k, set }) {
  const h = kksHist(k), tr = kksTrend(k), c = kksChart(k, 300, 90);
  const save = () => { if (mv(k.eoff) == null) return toast.error(t.kkNeedEoff); set({ hist: [...(k.hist || []), { t: Date.now(), eoff: mv(k.eoff), eon: mv(k.eon) }] }); return toast.success(t.kkSavedM); };
  return (
    <div className="kk-hist" data-testid="kks-history">
      <div className="row">
        <button className="btn btn-dark btn-sm" data-testid="kks-save-measure-btn" onClick={save}>💾 {t.kkSaveM}</button>
        <small data-testid="kks-hist-count">{t.kkHist}: <b>{h.length}</b></small>
        {tr && <small className={`kk-trend ${tr}`} data-testid="kks-trend">{tr === 'down' ? `↘ ${t.kkTrDown}` : tr === 'up' ? `↗ ${t.kkTrUp}` : `→ ${t.kkTrFlat}`}</small>}
      </div>
      {c && (
        <svg viewBox="0 0 300 90" preserveAspectRatio="none" data-testid="kks-chart">
          <line x1="0" x2="300" y1={c.lim} y2={c.lim} stroke="#dc2626" strokeDasharray="5 4" strokeWidth="1.5" />
          <text x="4" y={c.lim - 3} fontSize="9" fill="#dc2626">−850 mV</text>
          {c.on.length > 1 && <polyline points={c.on.map((p) => p.join(',')).join(' ')} fill="none" stroke="#94a3b8" strokeWidth="1.5" />}
          {c.off.length > 1 && <polyline points={c.off.map((p) => `${p[0]},${p[1]}`).join(' ')} fill="none" stroke="#0d9488" strokeWidth="2.2" />}
          {c.off.map((p, i) => <circle key={i} cx={p[0]} cy={p[1]} r="3.5" fill={p[2] <= KKS_LIMIT ? '#16a34a' : '#dc2626'} />)}
        </svg>
      )}
      {h.length > 0 && <small className="muted">{h.slice(-3).reverse().map((x) => `${new Date(x.t).toLocaleDateString()}: ${x.eoff} / ${x.eon ?? '–'} mV`).join(' · ')}</small>}
    </div>
  );
}

// cathodic protection measuring point: Eoff / Eon traffic light, insulating joint, valve cap / vent pipe, photo
export function KksBox({ t, obj, patch }) {
  if (obj.type !== 'kks') return null;
  const k = obj.kks || {}, ok = kksOk(k);
  const set = (p) => patch({ kks: { ...k, ...p, t: Date.now() }, ...('eoff' in p ? { state: kksOk({ ...k, ...p }) === false ? 'defect' : 'ok' } : {}) });
  return (
    <div className="fx-box ut-box" data-testid="kks-box">
      <b><ShieldCheck size={14} /> {t.kkTitle}</b>
      <div className="sw-grid">
        <label>Eoff (mV)<Num testid="kks-eoff" value={k.eoff} ph="-900" onChange={(eoff) => set({ eoff })} /></label>
        <label>Eon (mV)<Num testid="kks-eon" value={k.eon} ph="-1100" onChange={(eon) => set({ eon })} /></label>
      </div>
      {ok != null && <p className={`pr-alert ${ok ? 'ut-gas-ok' : 'ut-gas-alarm'}`} data-testid="kks-result">{ok ? '✅' : '❌'} Eoff {mv(k.eoff)} mV – {ok ? t.kkProt : t.kkUnprot} ({t.kkLimit} ≤ {KKS_LIMIT} mV Cu/CuSO4)</p>}
      <KksHistory t={t} k={k} set={set} />
      <small><b>{t.kkIj}</b></small>
      <Chips list={IJ_ST} value={k.ij} onPick={(ij) => set({ ij })} lbl={(x) => t['kkIj_' + x]} testid="kks-ij" />
      <small><b>{t.kkCap}</b></small>
      <Chips list={CAP_ST} value={k.cap} onPick={(cap) => set({ cap })} lbl={(x) => t['kkCap_' + x]} testid="kks-cap" />
      <div className="row">
        <label className="btn btn-outline btn-sm">📷 {t.dsPhoto}<input hidden type="file" accept="image/*" capture="environment" data-testid="kks-photo" onChange={async (e) => { const f = e.target.files?.[0]; if (f) set({ photo: (await compressImage(f, 1200, 0.75)).data }); }} /></label>
        {k.photo && <img src={k.photo} alt="" className="ds-ph" data-testid="kks-photo-thumb" />}
      </div>
    </div>
  );
}

// pinpointed cable fault: type, method, cable, voltage, phases / fibre, insulation resistance, depth, deviation to pre-location
export function FaultPinBox({ t, obj, patch, faults }) {
  if (obj.type !== 'cfault') return null;
  const c = obj.cf || {}, dev = cfDev(obj, faults);
  const set = (p) => patch({ cf: { ...c, ...p, t: Date.now() } });
  const ph = c.ph || [];
  return (
    <div className="fx-box ut-box" data-testid="cfault-box">
      <b><Zap size={14} /> {t.cfPinTitle}</b>
      {dev != null && <p className="sw-res" data-testid="cfault-dev">📏 {t.cfDev}: <b>{dev.toFixed(1).replace('.', ',')} m</b></p>}
      <small><b>{t.cfType}</b></small>
      <Chips list={CF_TYPES} value={c.type} onPick={(type) => set({ type })} lbl={(x) => t['cfT_' + x]} testid="cfault-type" />
      <small><b>{t.cfMethod}</b></small>
      <Chips list={CF_METHODS} value={c.method} onPick={(method) => set({ method })} lbl={(x) => t['cfM_' + x]} testid="cfault-method" />
      <div className="sw-grid">
        <label>{t.utCable}<input list="cf-cables" data-testid="cfault-cable" value={c.cable || ''} placeholder="NAYY 4x150" onChange={(e) => set({ cable: e.target.value })} /></label>
        <datalist id="cf-cables">{CF_CABLES.map((x) => <option key={x} value={x} />)}</datalist>
        <label>{t.cfVolt}<select data-testid="cfault-volt" value={c.volt || ''} onChange={(e) => set({ volt: e.target.value })}><option value="">–</option>{CF_VOLT.map((v) => <option key={v} value={v}>{t['cfV_' + v]}</option>)}</select></label>
        <label>{t.cfRiso}<span className="row"><Num testid="cfault-riso" value={c.riso} ph="0.5" onChange={(riso) => set({ riso })} />
          <select data-testid="cfault-unit" value={c.unit || 'MΩ'} onChange={(e) => set({ unit: e.target.value })}>{['MΩ', 'kΩ', 'Ω'].map((u) => <option key={u}>{u}</option>)}</select></span></label>
        <label>{t.cfDepth} (cm)<Num testid="cfault-depth" value={c.depth} ph="80" onChange={(depth) => set({ depth })} /></label>
        <label>{t.cfFiber}<input data-testid="cfault-fiber" value={c.fiber || ''} placeholder="z. B. 7, 8" onChange={(e) => set({ fiber: e.target.value })} /></label>
      </div>
      <small><b>{t.cfPhase}</b></small>
      <div className="chip-group">{CF_PHASES.map((p) => <button key={p} className={`chip seg ${ph.includes(p) ? 'on' : ''}`} data-testid={`cfault-ph-${p}`} onClick={() => set({ ph: ph.includes(p) ? ph.filter((x) => x !== p) : [...ph, p] })}>{p}</button>)}</div>
    </div>
  );
}

// line locating point: method, frequency, current, depth, warning tape, photo; depth can be written to the nearest pipe
export function LocateBox({ t, obj, patch, project, updateProject }) {
  if (obj.type !== 'locate') return null;
  const l = obj.loc || {};
  const set = (p) => patch({ loc: { ...l, ...p, t: Date.now() }, ...('depth' in p && num(p.depth) != null ? { depth: String(num(p.depth) / 100) } : {}) });
  const near = nearestPipe(project.pipes, project.fittings, [obj.lat, obj.lon], pipeLatLngs);
  const toPipe = () => { updateProject((p) => ({ ...p, pipes: p.pipes.map((x) => (x.id === near.pipe.id ? { ...x, depth: String(num(l.depth) / 100) } : x)) })); toast.success(t.loApplied); };
  return (
    <div className="fx-box ut-box" data-testid="locate-box">
      <b><Radar size={14} /> {t.loTitle}</b>
      <Chips list={LOC_METHODS} value={l.method} onPick={(method) => set({ method })} lbl={(x) => t['loM_' + x]} testid="locate-method" />
      <div className="sw-grid">
        <label>{t.loFreq} (kHz)<Num testid="locate-freq" value={l.freq} ph="33" onChange={(freq) => set({ freq })} /></label>
        <label>{t.loCur} (mA)<Num testid="locate-ma" value={l.ma} ph="120" onChange={(ma) => set({ ma })} /></label>
        <label>{t.cfDepth} (cm)<Num testid="locate-depth" value={l.depth} ph="90" onChange={(depth) => set({ depth })} /></label>
        <label>{t.loTapeD} (cm)<Num testid="locate-tape-depth" value={l.tapeD} ph="40" onChange={(tapeD) => set({ tapeD })} /></label>
      </div>
      <div className="chip-group"><small><b>{t.loTape}:</b></small>
        <button className={`chip seg ${l.tape === true ? 'on' : ''}`} data-testid="locate-tape-yes" onClick={() => set({ tape: true })}>{t.loYes}</button>
        <button className={`chip seg ${l.tape === false ? 'on' : ''}`} data-testid="locate-tape-no" onClick={() => set({ tape: false })}>{t.loNo}</button>
      </div>
      <div className="row">
        <label className="btn btn-outline btn-sm">📷 {t.dsPhoto}<input hidden type="file" accept="image/*" data-testid="locate-photo" onChange={async (e) => { const f = e.target.files?.[0]; if (f) set({ photo: (await compressImage(f, 1200, 0.75)).data }); }} /></label>
        {l.photo && <img src={l.photo} alt="" className="ds-ph" data-testid="locate-photo-thumb" />}
        {near && num(l.depth) != null && near.d < 15 && <button className="btn btn-dark btn-sm" data-testid="locate-to-pipe-btn" onClick={toPipe}>↧ {t.loToPipe} „{near.pipe.name || t.objKind_pipe}“ ({near.d.toFixed(1)} m)</button>}
      </div>
    </div>
  );
}

// tracer gas injection point
export function TracerBox({ t, obj, patch }) {
  if (obj.type !== 'tracer') return null;
  const g = obj.tr || {};
  const set = (p) => patch({ tr: { ...g, ...p } });
  return (
    <div className="fx-box ut-box" data-testid="tracer-box">
      <b>💨 {t.trgTitle}</b>
      <Chips list={['h2n2', 'he']} value={g.gas || 'h2n2'} onPick={(gas) => set({ gas })} lbl={(x) => t['trgGas_' + x]} testid="tracer-gas" />
      <div className="sw-grid">
        <label>{t.trgBar} (bar)<Num testid="tracer-bar" value={g.bar} ph="2" onChange={(bar) => set({ bar })} /></label>
        <label>{t.trgStart}<input type="datetime-local" data-testid="tracer-start" value={g.start || ''} onChange={(e) => set({ start: e.target.value })} /></label>
        <label>{t.trgQty}<Num testid="tracer-qty" value={g.qty} ph="0" onChange={(qty) => set({ qty })} /></label>
        <label>{t.trgUnit}<select data-testid="tracer-qty-unit" value={g.unit || 'm3'} onChange={(e) => set({ unit: e.target.value })}>{['m3', 'l', 'kg'].map((u) => <option key={u} value={u}>{u === 'm3' ? 'm³' : u}</option>)}</select></label>
        <label>{t.trgBottles}<Num testid="tracer-bottles" value={g.bottles} ph="1" onChange={(bottles) => set({ bottles })} /></label>
        <label>{t.trgEnd}<input type="datetime-local" data-testid="tracer-end" value={g.end || ''} onChange={(e) => set({ end: e.target.value })} /></label>
      </div>
      {!g.start && <button className="btn btn-outline btn-sm" data-testid="tracer-start-now" onClick={() => set({ start: new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16) })}>⏱ {t.trgNow}</button>}
    </div>
  );
}

// ground microphone / tracer detection point: acoustic intensity 0–100 % (+ optional tracer gas reading)
export function GmicBox({ t, obj, patch, project }) {
  if (obj.type !== 'gmic') return null;
  const g = obj.gm || {}, v = num(g.int) ?? 0;
  const all = (project.fittings || []).filter((f) => f.type === 'gmic' && num(f.gm?.int) != null);
  const max = all.reduce((a, f) => (!a || num(f.gm.int) > num(a.gm.int) ? f : a), null);
  const set = (p) => patch({ gm: { ...g, ...p, t: Date.now() } });
  return (
    <div className="fx-box ut-box" data-testid="gmic-box">
      <b>🎙️ {t.gmTitle}</b>
      <div className="row gm-row">
        <input type="range" min="0" max="100" step="1" data-testid="gmic-range" value={v} onChange={(e) => set({ int: e.target.value })} style={{ accentColor: v >= 70 ? '#dc2626' : v >= 40 ? '#f97316' : '#16a34a' }} />
        <Num testid="gmic-int" value={g.int} ph="%" onChange={(x) => set({ int: String(Math.min(100, Math.max(0, num(x) ?? 0))) })} /> <b data-testid="gmic-int-label">{v} %</b>
      </div>
      <label>{t.gmPpm} (ppm H2)<Num testid="gmic-ppm" value={g.ppm} ph="ppm" onChange={(ppm) => set({ ppm })} /></label>
      {max && <small className="muted" data-testid="gmic-max">{t.gmMax}: <b>{max.name}</b> ({max.gm.int} %){max.id === obj.id ? ` – ${t.gmThis}` : ''}</small>}
    </div>
  );
}

// unsurveyed draft point: warning + re-measure with the RTK receiver
export function PrecBox({ t, obj, patch }) {
  if (!obj.draft) return null;
  const go = async () => { const r = await measurePrecise(); if (r) { patch({ lat: r.gnss.lat, lon: r.gnss.lon, gnss: r.gnss, draft: r.draft || undefined }); toast.success(t.prMeasured); } };
  return (
    <div className="fx-box prec-draft-box" data-testid="prec-draft-box">
      <b>⚠️ {t.prDraft}</b>
      <small>{t.prNoRtk}</small>
      {extRtk() && <button className="btn btn-green btn-sm" data-testid="prec-remeasure-btn" onClick={go}>📡 {t.prMeasure}</button>}
    </div>
  );
}

// saved compass cross-bearing (read-only details)
export function XbBox({ t, obj, patch }) {
  if (obj.type !== 'xbear' || !obj.xb) return null;
  return (
    <div className="fx-box ut-box" data-testid="xb-box">
      <b>🧭 {t.xbTitle}</b>
      <small data-testid="xb-box-text">{xbText(obj, t)}</small>
      <small className="muted">{new Date(obj.xb.t).toLocaleString(t.locale)}</small>
      <div className="row">
        <label className="btn btn-outline btn-sm">📷 {obj.xb.photo ? t.xbPhoto : t.xbPhotoAdd}<input hidden type="file" accept="image/*" capture="environment" data-testid="xb-box-photo" onChange={async (e) => { const f = e.target.files?.[0]; if (f) patch({ xb: { ...obj.xb, photo: (await compressImage(f, 1200, 0.75)).data } }); }} /></label>
        {obj.xb.photo && <img src={obj.xb.photo} alt="" className="ds-ph" data-testid="xb-box-photo-thumb" />}
      </div>
    </div>
  );
}
